import { Bell } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useUnreadNotificationsCount } from '@/core/api/notifications';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
} from '@/shared/components/ui';
import type { RouteProp } from '@/shared/types/route-prop';
import {
  canReadService,
  resolveUserGroup,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';

import { CoordinateFormatSwitcher } from './coordinate-format-switcher';
import { LanguageSwitcher } from './language-switcher';
import { Logo } from './logo';
import { NavUser } from './nav-user';
import { SidebarLink } from './sidebar-link';
import { ThemeToggle } from './theme-toggle';

interface SidebarProps {
  routes: RouteProp[];
}

export const Sidebar = ({ routes }: SidebarProps) => {
  const { t } = useIntl();
  const location = useLocation();
  const { data: unreadCount = 0 } = useUnreadNotificationsCount();
  const { user } = useAuth();
  const group = resolveUserGroup(user);
  const canSeeNotifications = group ? canReadService(group, 'notifications') : false;
  const domain = resolveUserGroupDomain(user);

  const notificationsPath = domain === 'customer' ? '/c/notifications' : '/gmn/notifications';
  const isNotificationsActive = location.pathname.startsWith(notificationsPath);

  return (
    <ShadcnSidebar variant="sidebar">
      <SidebarHeader>
        <Logo />
      </SidebarHeader>
      <SidebarContent className="flex flex-col py-6 px-3">
        {/* Notifications - Top Level Item */}
        {canSeeNotifications && (
          <div className="mb-6">
            <SidebarMenuButton asChild data-active={isNotificationsActive}>
              <Link to={notificationsPath} className="flex justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <Bell
                    className={`size-[18px] ${isNotificationsActive ? 'text-primary' : 'text-muted-foreground'}`}
                  />
                  <span className="truncate">{t('common.notifications')}</span>
                </div>

                {unreadCount > 0 && (
                  <span
                    className={`
                      flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium leading-none
                      text-primary-foreground
                    `}
                  >
                    {unreadCount}
                  </span>
                )}
              </Link>
            </SidebarMenuButton>
          </div>
        )}

        {/* Navigation Label */}
        <div className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {t('common.navigation')}
        </div>

        {/* Navigation Items */}
        <SidebarMenu className="space-y-1">
          {routes.map((route) => (
            <SidebarLink
              key={route.to}
              route={route}
              currentPath={location.pathname}
              isActive={location.pathname.startsWith(route.to)}
            />
          ))}
        </SidebarMenu>
      </SidebarContent>

      {/* Footer Actions - alles in einem Container wie im Prototyp */}
      <SidebarFooter className="p-4 border-t border-border space-y-1">
        <LanguageSwitcher />
        <CoordinateFormatSwitcher />
        <ThemeToggle />
        {/* User Profile mit extra Abstand */}
        <div className="pt-2 mt-1">
          <NavUser />
        </div>
      </SidebarFooter>
    </ShadcnSidebar>
  );
};
